"use client";

import CurrencyInlinePill from "@/components/CurrencySelector/CurrencyInlinePill";
import CurrencyBar from "@/components/CurrencySelector/CurrencyBar";
import GeoPricingRibbonBanner from "@/components/GeoPricing/GeoPricingRibbonBanner";
import { cn } from "@/lib/utils";
import {
  DEFAULT_CURRENCY_BAR_LABEL,
  DEFAULT_CURRENCY_BAR_MOBILE_LABEL,
} from "@/components/CurrencySelector/CurrencyBar/constants";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import { getProgramPricingAmounts } from "@/lib/currency/getProgramPricingAmounts";
import { resolveCurrencySurfaces } from "@/lib/currency/resolveCurrencySurfaces";
import type { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import type { MultiCurrencyPdpConfig } from "@/lib/currency/types";
import type { MonarchCurrencySelectorProps } from "@/monarch/types/MonarchCurrencySelector";
import { resolveGeoPricingRibbonCopy } from "@/components/GeoPricing/utils/resolveGeoPricingRibbonCopy";

type ProgramCurrencyInlinePillProps = {
  pricingData?: Partial<ProgramPricingData> | null;
  location: string;
  className?: string;
  currencySelector?: MonarchCurrencySelectorProps | null;
  multiCurrencyConfig?: MultiCurrencyPdpConfig;
  showPrice?: boolean;
  variant?: "default" | "inverse";
  layout?: "default" | "bar" | "sideRail";
  useBarCurrencySelector?: boolean;
};

export function ProgramCurrencyInlinePill({
  pricingData,
  location,
  className,
  currencySelector: currencySelectorProp,
  multiCurrencyConfig: multiCurrencyConfigProp,
  showPrice = false,
  variant = "inverse",
  layout = "default",
  useBarCurrencySelector,
}: ProgramCurrencyInlinePillProps) {
  const { currencySelector, multiCurrency } = useProgramRedesignConfig();
  const resolvedSelector = currencySelectorProp ?? currencySelector;
  const amounts = getProgramPricingAmounts(pricingData);

  if (!resolvedSelector || !amounts) {
    return null;
  }

  return (
    <CurrencyInlinePill
      price={amounts.price}
      originalPrice={amounts.originalPrice}
      label={resolvedSelector.label ?? resolvedSelector.toggleText}
      mobileLabel={resolvedSelector.mobileLabel}
      tooltip={
        resolvedSelector.tooltip ??
        multiCurrencyConfigProp?.tooltipText ??
        multiCurrency?.tooltipText
      }
      showPrice={showPrice}
      variant={variant}
      layout={layout}
      useBarCurrencySelector={
        useBarCurrencySelector ?? resolvedSelector.placement === "currency-bar"
      }
      location={location}
      className={cn("[&_.currency-inline-pill-label]:text-sm", className)}
      multiCurrencyConfig={multiCurrencyConfigProp ?? multiCurrency}
      countryAutoDetection={resolvedSelector.countryAutoDetection}
    />
  );
}

type ProgramCurrencyBarSurfaceProps = {
  pricingData?: Partial<ProgramPricingData> | null;
  programIsEnrollable?: boolean;
  programIsRetired?: boolean;
};

export function useProgramCurrencySurfaces() {
  const { currencySelector } = useProgramRedesignConfig();

  return resolveCurrencySurfaces(currencySelector);
}

export function ProgramCurrencyBarSurface({
  pricingData,
  programIsEnrollable = true,
  programIsRetired = false,
}: ProgramCurrencyBarSurfaceProps) {
  const { currencySelector, multiCurrency, showSideRail } =
    useProgramRedesignConfig();
  const surfaces = resolveCurrencySurfaces(currencySelector);
  const amounts = getProgramPricingAmounts(pricingData);
  const geoPricing = pricingData?.geoPricing;
  const checkoutGeoPricing = pricingData?.checkoutGeoPricingEligibility;
  const isUserEligible = pricingData?.checkoutGeoPricingEligibility?.eligible;
  const ribbonBanner = pricingData?.geoPricing?.ribbonBanner;

  const hasMultiCurrencyEnabled =
    surfaces.stickyBar &&
    currencySelector &&
    currencySelector.placement === "currency-bar" &&
    amounts &&
    programIsEnrollable &&
    !programIsRetired;

  const isGeoPricingEnabled = Boolean(
    geoPricing !== undefined && isUserEligible && ribbonBanner,
  );

  let label = currencySelector?.label ?? DEFAULT_CURRENCY_BAR_LABEL;
  let mobileLabel =
    currencySelector?.mobileLabel ?? DEFAULT_CURRENCY_BAR_MOBILE_LABEL;
  let tooltip = currencySelector?.tooltip ?? multiCurrency?.tooltipText;
  let badgeText = currencySelector?.badgeText ?? "";

  if (isGeoPricingEnabled && checkoutGeoPricing && ribbonBanner) {
    label = resolveGeoPricingRibbonCopy(ribbonBanner.text, checkoutGeoPricing);
    mobileLabel = resolveGeoPricingRibbonCopy(
      ribbonBanner.text,
      checkoutGeoPricing,
    );
    tooltip = resolveGeoPricingRibbonCopy(
      ribbonBanner.tooltipText,
      checkoutGeoPricing,
    );
    badgeText = resolveGeoPricingRibbonCopy(
      ribbonBanner.badgeText,
      checkoutGeoPricing,
    );
  }

  if (!hasMultiCurrencyEnabled && isGeoPricingEnabled) {
    return (
      <GeoPricingRibbonBanner
        badgeText={badgeText}
        label={label}
        tooltip={tooltip}
        location="program-detail-page"
      />
    );
  }

  if (hasMultiCurrencyEnabled && amounts) {
    return (
      <CurrencyBar
        price={amounts.price}
        originalPrice={amounts.originalPrice}
        badgeText={badgeText}
        label={label}
        mobileLabel={mobileLabel}
        tooltip={tooltip}
        location="program-pdp-currency-bar"
        multiCurrencyConfig={multiCurrency}
        compactDismiss={!!showSideRail}
        countryAutoDetection={currencySelector?.countryAutoDetection}
      />
    );
  }

  return null;
}
